import { Reducer, combineReducers } from "redux";
import { createStandardAction } from "typesafe-actions";
import { ActionsUnion } from "../types";
import { omit } from "ramda";

interface BasketItem {
  productHandle: string;
  variantId: string;
  quantity: number;
}

export interface BasketState {
  items: {
    byId: {
      [key: string]: BasketItem;
    };
    allIds: string[];
  };
}

type ItemsState = BasketState["items"];

export const basketActions = {
  addItem: createStandardAction("BASKET/ADD_ITEM")<{
    productHandle: string;
    variantId: string;
  }>(),
  removeItem: createStandardAction("BASKET/REMOVE_ITEM")<{
    productHandle: string;
    variantId: string;
  }>(),
  updateQuantity: createStandardAction("BASKET/UPDATE_ITEM_QUANTITY")<{
    productHandle: string;
    variantId: string;
    quantity: number;
  }>(),
  emptyBasket: createStandardAction("BASKET/EMPTY_BASKET")<void>()
};

export const basketInitialState: BasketState = {
  items: {
    byId: {},
    allIds: []
  }
};

const getItemKey = (productHandle: string, variantId: string) =>
  `${productHandle}-${variantId}`;

const itemsById: Reducer<ItemsState["byId"], ActionsUnion> = (
  state = basketInitialState.items.byId,
  action
) => {
  let itemKey: string;

  switch (action.type) {
    case "BASKET/ADD_ITEM":
      itemKey = getItemKey(
        action.payload.productHandle,
        action.payload.variantId
      );
      const item = state[itemKey];
      if (!item) {
        return { ...state, [itemKey]: { ...action.payload, quantity: 1 } };
      }
      return {
        ...state,
        [itemKey]: { ...action.payload, quantity: item.quantity + 1 }
      };
    case "BASKET/UPDATE_ITEM_QUANTITY":
      itemKey = getItemKey(
        action.payload.productHandle,
        action.payload.variantId
      );
      return { ...state, [itemKey]: action.payload };
    case "BASKET/REMOVE_ITEM":
      itemKey = getItemKey(
        action.payload.productHandle,
        action.payload.variantId
      );
      return omit([itemKey], state);
    case "BASKET/EMPTY_BASKET":
      return {};
    default:
      return state;
  }
};

const allItems: Reducer<ItemsState["allIds"], ActionsUnion> = (
  state = basketInitialState.items.allIds,
  action
) => {
  let itemKey: string;

  switch (action.type) {
    case "BASKET/ADD_ITEM":
      itemKey = getItemKey(
        action.payload.productHandle,
        action.payload.variantId
      );
      if (state.includes(itemKey)) return state;
      return [...state, itemKey];
    case "BASKET/REMOVE_ITEM":
      itemKey = getItemKey(
        action.payload.productHandle,
        action.payload.variantId
      );
      return state.filter(id => id !== itemKey);
    case "BASKET/EMPTY_BASKET":
      return [];
    default:
      return state;
  }
};

const itemsReducer: Reducer<
  BasketState["items"],
  ActionsUnion
> = combineReducers({
  byId: itemsById,
  allIds: allItems
});

export const basketReducer: Reducer<
  BasketState,
  ActionsUnion
> = combineReducers({
  items: itemsReducer
});
